import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface CreateBookingRequest {
  bookingType: BookingType;
  customerId: string;
  customerIsTraveler: boolean;
  tourName: string;
  tourCode: string;
  country: string;
  wholesaleId: string;
  departureDate: string;
  returnDate: string;
  pax: number;
  tourPrice: number;
  discount: number;
  totalAmount: number;
  estimatedExpense: number;
  companyCommission: number;
  employeeCommission: number;
  specialCommission: number;
}

export interface CreateDomesticBookingRequest {
  customerId: string;
  tourId: string;
  customerIsTraveler: boolean;
  pax: number;
  discount: number;
}

export interface CreatedBooking {
  id: string;
  bookingNo: string;
}

export interface BookingListItem {
  id: string;
  bookingNo: string;
  bookingType: BookingType;
  tourId: string | null;
  tourName: string;
  tourCode: string | null;
  country: string | null;
  wholesaleId: string | null;
  booking_wholesale: { id: string; name: string } | null;
  bookingDate: string;
  departureDate: string;
  returnDate: string | null;
  pax: number;
  totalAmount: number | string;
  estimatedExpense: number | string;
  com_company: number | string | null;
  com_sale: number | string | null;
  com_special: number | string | null;
  commissionAttachmentPath: string | null;
  collaboratorId: string | null;
  collaborator: { id: string; firstName: string; lastName: string } | null;
  tour: {
    status: string;
    createdById: string;
    administrators: Array<{ userId: string }>;
  } | null;
  pricePerPerson: number | string;
  discount?: number | string | null;
  paymentStatus: 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERPAID';
  status: 'DRAFT' | 'PENDING_CONFIRMATION' | 'CONFIRMED' | 'COMPLETED' | 'REFUND' | 'REFUND_PENDING' | 'CANCELLED' | 'DELETED';
  booking_customer: {
    id: string;
    customerCode: string;
    customerType: 'INDIVIDUAL' | 'CORPORATE';
    name: string;
    taxId: string | null;
    phone: string | null;
    email: string | null;
  };
  salebookings: { id: string; firstName: string; lastName: string };
  createdBy: { id: string; firstName: string; lastName: string };
  booking_memberTravelers: TourMemberItem[];
  booking_payments: CustomerPaymentItem[];
  booking_refunds: BookingRefundItem[];
  expenseRequests: ExpenseRequestItem[];
  booking_commissions: BookingCommissionItem[];
  commissionRecoveryBalances?: Array<{
    recipientUserId: string;
    outstandingAmount: number | string;
  }>;
  approvedFinancialAdjustmentTotals?: {
    expenseBudgetDelta: number | string;
    customerChargeDelta: number | string;
    companyAdvanceAmount: number | string;
  };
  expenseAvailability?: {
    verifiedTotal: number | string;
    approvedCompanyAdvance: number | string;
    expenseTotal: number | string;
    availableAmount: number | string;
  };
  createdAt: string;
}

export type BookingType = 'JOIN_TOUR' | 'GROUP_TOUR';

export type PaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'CREDIT_CARD';
export type PaymentStage = 'BOOKING' | 'DEPOSIT' | 'BALANCE' | 'FULL_PAYMENT';
export type CreditCardFeePercent = 0 | 3 | 4;
export type CreditCardFeeResponsibility = 'CUSTOMER' | 'COMPANY';

export interface CustomerPaymentItem {
  id: string;
  paymentNo: string;
  installmentNo: number;
  paymentStage: PaymentStage;
  payerName: string;
  amount: number | string | null;
  paymentDate: string;
  paymentTime: string;
  paymentMethod: PaymentMethod;
  creditCardFeePercent: CreditCardFeePercent;
  creditCardFeeResponsibility: CreditCardFeeResponsibility | null;
  creditCardFeeAmount: number | string | null;
  customerPaidAmount: number | string | null;
  withholdingTaxAmount: number | string;
  status: 'WAITING_VERIFY' | 'VERIFIED' | 'REJECTED' | 'CANCELLED' | 'DELETED';
  slipPath: string | null;
  receiptUrl: string | null;
  quotationUrl: string | null;
}

export interface SavePaymentRequest {
  payerName: string;
  paymentStage: PaymentStage;
  paymentMethod: PaymentMethod;
  creditCardFeePercent: CreditCardFeePercent;
  creditCardFeeResponsibility: CreditCardFeeResponsibility | null;
  amount: number;
  withholdingTaxAmount: number;
  paidDate: string;
  paymentTime: string;
}

export interface BookingRefundItem {
  id: string;
  refundNo: string;
  refundType: 'PARTIAL' | 'FULL';
  namerefund: string;
  bankName: string | null;
  account_number: string;
  amount: number | string;
  reason: string;
  attachmentPath: string | null;
  status: 'DRAFT' | 'SUPERVISOR_REVIEW' | 'WAITING_PAYMENT' | 'PAID' | 'REJECTED' | 'CANCELLED';
  paymentTransaction: {
    amount: number | string;
    status: 'PAID' | 'CANCELLED';
    receiptUrl: string | null;
    transferredAt: string | null;
    paymentDate: string;
  } | null;
  requestedAt: string | null;
  createdAt: string;
}

export interface SaveRefundRequest {
  refundName: string;
  bankName: string;
  accountNumber: string;
  refundType: BookingRefundItem['refundType'];
  amount: number;
  reason: string;
}

export interface ExpenseRequestItem {
  id: string;
  expenseNo: string;
  expenseNameSnapshot: string;
  wholesaleId: string | null;
  wholesaleNameSnapshot: string | null;
  wholesale: { id: string; name: string } | null;
  description: string;
  amount: number | string | null;
  actualTransferAmount: number | string | null;
  source: 'MANUAL' | 'BOOKING_ESTIMATE';
  attachmentPath: string | null;
  receiptUrl: string | null;
  receiptPath: string | null;
  receiptSubmittedAt: string | null;
  status: 'DRAFT' | 'SUPERVISOR_REVIEW' | 'WAITING_PAYMENT' | 'WAITING_CLEARING' | 'ACCOUNTING_REVIEW_RECEIPT' | 'VERIFIED' | 'REJECTED' | 'CANCELLED' | 'DELETED';
  createdAt: string;
  requestedBy: { firstName: string; lastName: string };
  paymentTransactions: Array<{
    amount: number | string;
    status: 'PAID' | 'CANCELLED';
  }>;
}

export interface SaveExpenseRequest {
  expenseNameSnapshot: string;
  wholesaleId?: string;
  description: string;
  actualTransferAmount: number;
  submitForReview: boolean;
}

export interface BookingCommissionItem {
  id: string;
  commissionNo: string;
  recipientRole: 'SALE' | 'CARETAKER';
  pax: number;
  saleCommissionRate: number | string;
  specialCommissionRate: number | string;
  saleCommissionAmount: number | string;
  specialCommissionAmount: number | string;
  totalAmount: number | string;
  attachmentPath: string | null;
  status: 'DRAFT' | 'SUPERVISOR_REVIEW' | 'WAITING_PAYMENT' | 'VERIFIED' | 'REJECTED' | 'CANCELLED' | 'DELETED';
  submittedAt: string | null;
  createdAt: string;
  sale: {
    firstName: string;
    lastName: string;
  };
  requestedBy: {
    firstName: string;
    lastName: string;
  } | null;
  paymentTransaction: {
    amount: number | string;
    status: 'PAID' | 'CANCELLED';
  } | null;
  recoverySources: CommissionRecoveryItem[];
}

export interface CommissionRecoveryItem {
  id: string;
  originalAmount: number | string;
  recoveryAmount: number | string;
  settledAmount: number | string;
  remainingAmount: number | string;
  status: 'PENDING' | 'PARTIAL' | 'SETTLED' | 'WAIVED' | 'CANCELLED';
  reason: string;
  createdAt: string;
  bookingRefund: {
    refundNo: string;
    refundType: 'PARTIAL' | 'FULL';
    amount: number | string;
    status: BookingRefundItem['status'];
  };
  applications: CommissionRecoveryApplicationItem[];
}

export interface CommissionRecoveryApplicationItem {
  id: string;
  targetType: 'BOOKING_COMMISSION' | 'TOUR_COMMISSION';
  amount: number | string;
  status: 'RESERVED' | 'APPLIED' | 'RELEASED';
  appliedAt: string | null;
  createdAt: string;
  bookingCommission: { commissionNo: string } | null;
  tourCommission: { commissionNo: string } | null;
}

export interface SaveCommissionRequest {
  recipientRole: 'SALE' | 'CARETAKER';
  saleCommission: number;
  specialCommission: number;
  submitForReview: boolean;
}

export interface TourMemberItem {
  id: string;
  travelerNo: number;
  name: string;
  idcard: string | null;
  phone: string | null;
  passportPath: string | null;
  isLeadTraveler: boolean;
  status: 'DRAFT' | 'COMPLETED' | 'DELETED';
}

export interface SaveTourMemberRequest {
  name: string;
  idcard: string;
  phone: string;
  isLeadTraveler: boolean;
}

interface CreateBookingResponse {
  message: string;
  booking: CreatedBooking;
}

interface BookingsResponse {
  bookings: BookingListItem[];
}

@Injectable({ providedIn: 'root' })
export class BookingService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/bookings`;

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }

  async getAll(bookingType?: BookingType): Promise<BookingListItem[]> {
    const response = await firstValueFrom(
      this.http.get<BookingsResponse>(this.apiUrl, {
        withCredentials: true,
        params: bookingType ? { bookingType } : undefined,
      }),
    );
    return response.bookings;
  }

  async getWholesales(): Promise<Array<{ id: string; name: string }>> {
    const response = await firstValueFrom(
      this.http.get<{ wholesales: Array<{ id: string; name: string }> }>(
        `${this.apiUrl}/wholesales`,
        { withCredentials: true },
      ),
    );
    return response.wholesales;
  }

  async getExpenseTypes(): Promise<Array<{ id: string; name: string }>> {
    const response = await firstValueFrom(
      this.http.get<{ expenseTypes: Array<{ id: string; name: string }> }>(
        `${this.apiUrl}/expense-types`,
        { withCredentials: true },
      ),
    );
    return response.expenseTypes;
  }

  async getById(id: string): Promise<BookingListItem | undefined> {
    const response = await firstValueFrom(
      this.http.get<{ booking: BookingListItem }>(`${this.apiUrl}/${id}`, {
        withCredentials: true,
      }),
    );
    return response.booking;
  }

  async create(input: CreateBookingRequest): Promise<CreatedBooking> {
    const response = await firstValueFrom(
      this.http.post<CreateBookingResponse>(this.apiUrl, input, { withCredentials: true }),
    );
    return response.booking;
  }

  async createDomestic(input: CreateDomesticBookingRequest): Promise<CreatedBooking> {
    const response = await firstValueFrom(
      this.http.post<{ booking: CreatedBooking }>(`${this.apiUrl}/domestic`, input, {
        withCredentials: true,
      }),
    );
    return response.booking;
  }

  async update(id: string, input: CreateBookingRequest): Promise<BookingListItem> {
    const response = await firstValueFrom(
      this.http.put<{ message: string; booking: BookingListItem }>(`${this.apiUrl}/${id}`, input, {
        withCredentials: true,
      }),
    );
    return response.booking;
  }

  async saveCommissionAttachment(bookingId: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append('commissionAttachment', file);
    const response = await firstValueFrom(
      this.http.put<{ attachmentPath: string }>(
        `${this.apiUrl}/${bookingId}/commission-attachment`,
        formData,
        { withCredentials: true },
      ),
    );
    return response.attachmentPath;
  }

  async updateDomestic(
    id: string,
    input: { customerId: string; pax: number; discount: number },
  ): Promise<BookingListItem> {
    const response = await firstValueFrom(
      this.http.put<{ message: string; booking: BookingListItem }>(
        `${this.apiUrl}/${id}/domestic`, input, { withCredentials: true },
      ),
    );
    return response.booking;
  }

  async delete(id: string): Promise<BookingListItem> {
    const response = await firstValueFrom(
      this.http.delete<{ message: string; booking: BookingListItem }>(`${this.apiUrl}/${id}`, {
        withCredentials: true,
      }),
    );
    return response.booking;
  }

  async closeJoinCase(id: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.apiUrl}/${id}/close-case`, {}, { withCredentials: true }),
    );
  }

  async createTraveler(
    bookingId: string,
    input: SaveTourMemberRequest,
    passport: File,
  ): Promise<TourMemberItem> {
    return this.saveTraveler(`${this.apiUrl}/${bookingId}/travelers`, input, passport, 'post');
  }

  async updateTraveler(
    bookingId: string,
    travelerId: string,
    input: SaveTourMemberRequest,
    passport: File | null,
  ): Promise<TourMemberItem> {
    return this.saveTraveler(
      `${this.apiUrl}/${bookingId}/travelers/${travelerId}`,
      input,
      passport,
      'put',
    );
  }

  private async saveTraveler(
    url: string,
    input: SaveTourMemberRequest,
    passport: File | null,
    method: 'post' | 'put',
  ): Promise<TourMemberItem> {
    const formData = new FormData();
    formData.append('name', input.name);
    formData.append('idcard', input.idcard);
    formData.append('phone', input.phone);
    formData.append('isLeadTraveler', String(input.isLeadTraveler));
    if (passport) formData.append('passport', passport);
    const response = await firstValueFrom(
      this.http.request<{ traveler: TourMemberItem }>(method, url, {
        body: formData,
        withCredentials: true,
      }),
    );
    return response.traveler;
  }

  async deleteTraveler(bookingId: string, travelerId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/travelers/${travelerId}`, {
        withCredentials: true,
      }),
    );
  }

  createPayment(bookingId: string, input: SavePaymentRequest, slip: File): Promise<CustomerPaymentItem> {
    return this.savePayment(`${this.apiUrl}/${bookingId}/payments`, input, slip, 'post');
  }

  updatePayment(
    bookingId: string,
    paymentId: string,
    input: SavePaymentRequest,
    slip: File | null,
  ): Promise<CustomerPaymentItem> {
    return this.savePayment(`${this.apiUrl}/${bookingId}/payments/${paymentId}`, input, slip, 'put');
  }

  private async savePayment(
    url: string,
    input: SavePaymentRequest,
    slip: File | null,
    method: 'post' | 'put',
  ): Promise<CustomerPaymentItem> {
    const formData = new FormData();
    formData.append('payerName', input.payerName);
    formData.append('paymentStage', input.paymentStage);
    formData.append('paymentMethod', input.paymentMethod);
    formData.append('creditCardFeePercent', String(input.creditCardFeePercent));
    if (input.creditCardFeeResponsibility) {
      formData.append('creditCardFeeResponsibility', input.creditCardFeeResponsibility);
    }
    formData.append('amount', String(input.amount));
    formData.append('withholdingTaxAmount', String(input.withholdingTaxAmount));
    formData.append('paidDate', input.paidDate);
    formData.append('paymentTime', input.paymentTime);
    if (slip) formData.append('paymentSlip', slip);
    const response = await firstValueFrom(
      this.http.request<{ payment: CustomerPaymentItem }>(method, url, {
        body: formData,
        withCredentials: true,
      }),
    );
    return response.payment;
  }

  async deletePayment(bookingId: string, paymentId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/payments/${paymentId}`, {
        withCredentials: true,
      }),
    );
  }

  async createRefund(
    bookingId: string,
    input: SaveRefundRequest,
    attachment: File,
  ): Promise<BookingRefundItem> {
    const formData = new FormData();
    formData.append('refundName', input.refundName);
    formData.append('bankName', input.bankName);
    formData.append('accountNumber', input.accountNumber);
    formData.append('refundType', input.refundType);
    formData.append('amount', String(input.amount));
    formData.append('reason', input.reason);
    formData.append('refundAttachment', attachment);
    const response = await firstValueFrom(
      this.http.post<{ refund: BookingRefundItem }>(`${this.apiUrl}/${bookingId}/refunds`, formData, {
        withCredentials: true,
      }),
    );
    return response.refund;
  }

  async createExpense(
    bookingId: string,
    input: SaveExpenseRequest,
    attachment: File,
  ): Promise<ExpenseRequestItem> {
    const formData = new FormData();
    formData.append('expenseNameSnapshot', input.expenseNameSnapshot);
    if (input.wholesaleId) formData.append('wholesaleId', input.wholesaleId);
    formData.append('description', input.description);
    formData.append('actualTransferAmount', String(input.actualTransferAmount));
    formData.append('submitForReview', String(input.submitForReview));
    formData.append('expenseAttachment', attachment);
    const response = await firstValueFrom(
      this.http.post<{ expense: ExpenseRequestItem }>(`${this.apiUrl}/${bookingId}/expenses`, formData, {
        withCredentials: true,
      }),
    );
    return response.expense;
  }

  async updateExpense(
    bookingId: string,
    expenseId: string,
    input: SaveExpenseRequest,
    attachment: File | null,
  ): Promise<ExpenseRequestItem> {
    const formData = new FormData();
    formData.append('expenseNameSnapshot', input.expenseNameSnapshot);
    if (input.wholesaleId) formData.append('wholesaleId', input.wholesaleId);
    formData.append('description', input.description);
    formData.append('actualTransferAmount', String(input.actualTransferAmount));
    formData.append('submitForReview', String(input.submitForReview));
    if (attachment) formData.append('expenseAttachment', attachment);
    const response = await firstValueFrom(
      this.http.put<{ expense: ExpenseRequestItem }>(
        `${this.apiUrl}/${bookingId}/expenses/${expenseId}`,
        formData,
        { withCredentials: true },
      ),
    );
    return response.expense;
  }

  async deleteExpense(bookingId: string, expenseId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/expenses/${expenseId}`, {
        withCredentials: true,
      }),
    );
  }

  async submitExpenseReceipt(
    bookingId: string,
    expenseId: string,
    receipt: File,
  ): Promise<ExpenseRequestItem> {
    const formData = new FormData();
    formData.append('expenseReceipt', receipt);
    const response = await firstValueFrom(
      this.http.post<{ expense: ExpenseRequestItem }>(
        `${this.apiUrl}/${bookingId}/expenses/${expenseId}/receipt`,
        formData,
        { withCredentials: true },
      ),
    );
    return response.expense;
  }

  createCommission(
    bookingId: string,
    input: SaveCommissionRequest,
  ): Promise<BookingCommissionItem> {
    return this.saveCommission(`${this.apiUrl}/${bookingId}/commissions`, input, 'post');
  }

  updateCommission(
    bookingId: string,
    commissionId: string,
    input: SaveCommissionRequest,
  ): Promise<BookingCommissionItem> {
    return this.saveCommission(
      `${this.apiUrl}/${bookingId}/commissions/${commissionId}`,
      input,
      'put',
    );
  }

  private async saveCommission(
    url: string,
    input: SaveCommissionRequest,
    method: 'post' | 'put',
  ): Promise<BookingCommissionItem> {
    const response = await firstValueFrom(
      this.http.request<{ commission: BookingCommissionItem }>(method, url, {
        body: input,
        withCredentials: true,
      }),
    );
    return response.commission;
  }

  async deleteCommission(bookingId: string, commissionId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/commissions/${commissionId}`, {
        withCredentials: true,
      }),
    );
  }

  async deleteRefund(bookingId: string, refundId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${bookingId}/refunds/${refundId}`, {
        withCredentials: true,
      }),
    );
  }
}
